/**
 * TEMP-H-001: useIterations Hook
 * 
 * React Query hook for fetching stage iterations with accepted markers.
 * Returns iterations ordered newest to oldest with metadata.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { executionAPI } from '@/lib/execution-api';
import { executionKeys, type StageIterations, APIClientError } from '@/lib/api-types';
import type { StageType } from '@/types/execution';

interface UseIterationsOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

export function useIterations(
  executionId: string,
  stage: StageType,
  options: UseIterationsOptions = {}
): UseQueryResult<StageIterations, APIClientError> {
  const { enabled = true, refetchInterval } = options;

  return useQuery({
    queryKey: executionKeys.stageIterations(executionId, stage),
    queryFn: () => executionAPI.getStageIterations(executionId, stage),
    enabled: enabled && !!executionId && !!stage,
    refetchInterval,
    staleTime: 30 * 1000, // 30 seconds - iterations change more frequently
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404 (execution/stage not found) or 403 (unauthorized)
      if (error.status === 404 || error.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Utility hook to get the accepted iteration for a stage
 */
export function useAcceptedIteration(executionId: string, stage: StageType) {
  const { data: iterations, ...queryResult } = useIterations(executionId, stage);
  
  const acceptedIteration = iterations?.items.find(item => item.accepted);
  
  return {
    ...queryResult,
    data: acceptedIteration || null,
    hasAccepted: !!acceptedIteration,
  };
}

export default useIterations;